import React, { useState } from 'react';
import {
  Button,
  Image,
  List,
  Message,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Segment,
} from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, get } from 'lodash';
import {
  doApproveKyb,
  doRejectKyb,
  doSetKybStatusPending,
  setKybApproveConfirmModalShown,
  setKybDetailModalShown,
  setKybEditBankAccountModalShown,
  setKybEditKtpModalShown,
  setKybRejectConfirmModalShown,
  setKybReviewDetailsModalShown,
  submitKybEditBankAccount,
  submitKybEditKtp,
} from 'state/kyb/actions';
import KybRejectConfirmationModal from 'pages/Kyb/KybRejectConfirmationModal';
import KybApproveConfirmationModal from 'pages/Kyb/KybApproveConfirmationModal';
import ApprovalStatusMessage from 'pages/Kyb/ApprovalStatusMessage';
import EditBankAccountModal from 'pages/Kyb/EditBankAccountModal';
import KtpImagePreviewModal from 'pages/Kyb/KtpImagePreviewModal';
import KybReviewDetailsModal from 'pages/Kyb/KybReviewDetailsModal';
import EditKtpModal from 'pages/Kyb/EditKtpModal';
import { format } from 'date-fns';

function BusinessDetailModal() {
  const dispatch = useDispatch();
  const {
    business,
    modalShown,
    loadingStates,
    errorMessages,
    ktp,
    bankAccount,
    approveModal,
    approvalLogs,
    rejectModal,
    editBankAccountModal,
    reviewDetailsModal,
    editKtpModal,
  } = useSelector(state => state.kyb.businessDetail);

  const { banks } = useSelector(state => state.global);

  const onClose = () => dispatch(setKybDetailModalShown(false));

  const getBusinessNameOrOwnerName = () => {
    if (!business) {
      return '';
    }
    return (business.name || '').trim() !== ''
      ? business.name
      : business.owner_name;
  };

  const renderBusinessDetail = () => {
    return (
      <Segment loading={loadingStates.business}>
        {!!business && (
          <List>
            <List.Item>
              <strong>Business ID:</strong> {business.id}
            </List.Item>
            <List.Item>
              <strong>Business Name:</strong> {business.name}
            </List.Item>
            <List.Item>
              <strong>Owner Name:</strong> {business.owner_name}
            </List.Item>
            <List.Item>
              <strong>Business Address:</strong> {business.address}
            </List.Item>
            <List.Item>
              <strong>Registered Merchant Email:</strong> {business.owner_email}
            </List.Item>
            <List.Item>
              <strong>Registered Phone Number:</strong> {business.phone}
            </List.Item>
            <List.Item>
              <strong>Registered Date:</strong>{' '}
              {format(new Date(business.registered_date), 'dd-MM-yyyy')}
            </List.Item>
            <List.Item>
              <strong>Expired At:</strong>{' '}
              {format(new Date(business.expired_at), 'dd-MM-yyyy')}
            </List.Item>
          </List>
        )}
      </Segment>
    );
  };

  const [ktpImagePreviewShown, setKtpImagePreviewShown] = useState(false);
  const onEditKtpDetailsButtonClicked = () =>
    dispatch(setKybEditKtpModalShown(true));

  const renderKtpDetails = () => {
    return (
      <Segment loading={loadingStates.ktp}>
        <h3>KTP Details</h3>
        {!!ktp && (
          <>
            <List>
              <List.Item>
                <strong>KTP Number:</strong> {ktp.number}
              </List.Item>
              <List.Item>
                <strong>KTP Name:</strong> {ktp.name}
              </List.Item>
            </List>
            {!!ktp.image && (
              <>
                <Image
                  src={ktp.image}
                  size="small"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setKtpImagePreviewShown(true)}
                />
                <small>
                  <em>Click image to enlarge</em>
                </small>
              </>
            )}
          </>
        )}
        <br />
        {get(business, 'kyb_status', '') === 'rejected' && (
          <Button primary onClick={onEditKtpDetailsButtonClicked}>
            Edit KTP Details
          </Button>
        )}
      </Segment>
    );
  };

  const getBankName = bankId => banks.find(b => b.id === bankId).name;
  const onEditBankAccountButtonClicked = () =>
    dispatch(setKybEditBankAccountModalShown(true));

  const renderBankAccount = () => {
    return (
      <Segment loading={loadingStates.bankAccount}>
        <h3>Bank Account Details</h3>
        {!!bankAccount && (
          <List>
            <List.Item>
              <strong>Bank Name:</strong> {getBankName(bankAccount.bank_id)}
            </List.Item>
            <List.Item>
              <strong>Bank Account Number:</strong>{' '}
              {bankAccount.account_holder_number}
            </List.Item>
            <List.Item>
              <strong>Bank Account Holder Name:</strong>{' '}
              {bankAccount.account_holder_name}
            </List.Item>
          </List>
        )}
        {get(business, 'kyb_status', '') === 'rejected' && (
          <Button primary onClick={onEditBankAccountButtonClicked}>
            Edit Bank Account
          </Button>
        )}
      </Segment>
    );
  };

  const renderModalContent = () => {
    return (
      <>
        {renderBusinessDetail()}
        <Message info>
          Personal will need the same name across KTP, NPWP, and Bank Account.
          Corporate will need the same name across NPWP and Bank Account
        </Message>
        <Segment.Group horizontal>
          {renderKtpDetails()}
          {renderBankAccount()}
        </Segment.Group>
        <Segment loading={loadingStates.log}>
          {!!business && (
            <ApprovalStatusMessage
              currentStatus={business.kyb_status}
              approvalLogs={approvalLogs}
            />
          )}
        </Segment>
      </>
    );
  };

  const onApproveButtonClicked = () =>
    dispatch(setKybApproveConfirmModalShown(true));
  const onKybApproveModalConfirmed = () => dispatch(doApproveKyb(business.id));
  const onKybApproveModalClosed = () =>
    dispatch(setKybApproveConfirmModalShown(false));

  const onRejectButtonClicked = () =>
    dispatch(setKybRejectConfirmModalShown(true));
  const onKybRejectModalClosed = () =>
    dispatch(setKybRejectConfirmModalShown(false));
  const onKybRejectModalConfirmed = reason =>
    dispatch(doRejectKyb(business.id, reason));

  const onEditBankAccountSubmit = data =>
    dispatch(submitKybEditBankAccount(business.id, data));
  const onEditBankAccountClosed = () =>
    dispatch(setKybEditBankAccountModalShown(false));

  const onUpdateButtonClicked = () =>
    dispatch(setKybReviewDetailsModalShown(true));
  const onReviewDetailsModalClosed = () =>
    dispatch(setKybReviewDetailsModalShown(false));
  const onReviewDetailsModalConfirmed = () =>
    dispatch(doSetKybStatusPending(business.id));

  const onEditKtpModalClosed = () => dispatch(setKybEditKtpModalShown(false));
  const onEditKtpModalSubmit = data =>
    dispatch(submitKybEditKtp(business.id, data));

  return (
    <>
      {!!business && (
        <>
          <KybApproveConfirmationModal
            onClose={onKybApproveModalClosed}
            onConfirm={onKybApproveModalConfirmed}
            isLoading={approveModal.isLoading}
            businessOrOwnerName={getBusinessNameOrOwnerName()}
            shown={approveModal.shown}
          />
          <KybRejectConfirmationModal
            onClose={onKybRejectModalClosed}
            onConfirm={onKybRejectModalConfirmed}
            shown={rejectModal.shown}
            isLoading={rejectModal.isLoading}
          />
          <EditBankAccountModal
            onSubmit={onEditBankAccountSubmit}
            isLoading={editBankAccountModal.isLoading}
            onClose={onEditBankAccountClosed}
            shown={editBankAccountModal.shown}
          />
          {!!ktp && (
            <KtpImagePreviewModal
              onClose={() => setKtpImagePreviewShown(false)}
              ktp={ktp}
              open={ktpImagePreviewShown}
            />
          )}
          <KybReviewDetailsModal
            onConfirm={onReviewDetailsModalConfirmed}
            onClose={onReviewDetailsModalClosed}
            open={reviewDetailsModal.shown}
            isLoading={reviewDetailsModal.isLoading}
          />
          <EditKtpModal
            onClose={onEditKtpModalClosed}
            onSubmit={onEditKtpModalSubmit}
            isLoading={editKtpModal.isLoading}
            shown={editKtpModal.shown}
          />
        </>
      )}

      <Modal open={modalShown} onClose={onClose}>
        <ModalHeader>Business KYB Details</ModalHeader>
        <ModalContent>
          <Message negative hidden={isEmpty(errorMessages)}>
            <Message.Header>Error</Message.Header>
            <Message.List>
              {errorMessages.map(error => (
                <Message.Item>{error}</Message.Item>
              ))}
            </Message.List>
          </Message>
          {renderModalContent()}
        </ModalContent>
        <ModalActions>
          {!['approved', 'rejected', ''].includes(
            get(business, 'kyb_status', '')
          ) && (
            <>
              <Button positive onClick={onApproveButtonClicked}>
                Approve
              </Button>
              <Button negative onClick={onRejectButtonClicked}>
                Reject
              </Button>
            </>
          )}
          {get(business, 'kyb_status', '') === 'rejected' && (
            <Button primary onClick={onUpdateButtonClicked}>
              Update
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </ModalActions>
      </Modal>
    </>
  );
}

export default BusinessDetailModal;
